import React, { useState } from 'react';
import './ES.css';
import { NavLink } from 'react-router-dom';
import { MathJax, MathJaxContext } from "better-react-mathjax";
import PageNavigator from "../../../components/PageNavigator";
// import ExampleBox from "../../../components/ExampleBox";


function ES1() {
  return (
        <div className='ES'>
        <PageNavigator group="ES" />
        <p className='heading1'>Event Study Basics</p>
        <MathJaxContext>
            <p className='heading2'>Price Reaction to News</p>
            <p>
            Suppose Big Pharma Co. announces that one of its drugs in development failed to obtain FDA approval. 
            After the announcement, Big Pharma Co.'s stock price drops. On the same day, new tariffs are announced, and regulators unveil a plan 
            to set lower medicare drug price caps. 
            </p>
            <p>
            We want to estimate how much of Big Pharma Co.'s stock price decline was caused by investors learning that the drug failed to obtain FDA approval. But, the announcements of tariffs and lower medicare drug price caps were also bad for Big Pharma Co. and likely created their own 
            negative price reactions at the same time as the announcement of the failed drug. 
            </p>
            <p> 
            The purpose of an event study is to estimate the portion of the stock price reaction attributable to firm-specific news (the failed drug) after controlling for broader market and industry movements (tariffs and lower medicare drug price caps).
            </p>
            <p className='heading2'>Training</p>
            <p>
            Before we can untangle industry and market effects from the stock price movements of Big Pharma Co., we need to establish a baseline for how Big Pharma Co.'s stock price tends to move with the overall market and the pharma industry. 
            We accomplish this by training a model to explain Big Pharma Co.'s stock return as a function of market and industry returns. 
            If we think back to the lesson on <NavLink className='inline-link' to="/corporate_finance/risk/factor_models">Factor Models</NavLink>, we can use <NavLink className='inline-link' to="/stats/multivariate_regression">Regression with Multiple Variables</NavLink> to 
            estimate such a model. If we denote the company's stock return at time <MathJax inline>{"\\( t \\)"}</MathJax> as <MathJax inline>{"\\( R_t \\)"}</MathJax>, the market return as <MathJax inline>{"\\( R_{mt} \\)"}</MathJax>, and the return on an industry index as <MathJax inline>{"\\( R_{it} \\)"}</MathJax>, 
            we can estimate the following regression:
              <MathJax className='math-container'>
                {`\\[
                  \\begin{aligned}
                      R_t = \\alpha + \\beta_{\\text{Market}}R_{mt} + \\beta_{\\text{Industry}}R_{it} + \\varepsilon_t
                  \\end{aligned}
                \\]`}   
              </MathJax> 
            The estimated coefficients measure how sensitive Big Pharma Co.'s stock has historically been to movements in the overall market and the pharmaceutical industry. We use those historical relationships to estimate how the stock would normally have responded on the event date.
            </p>
            <p>
            This type of "market + industry" model is common in event studies. But, any number and combination of explanatory variables may be used to predict the company's stock return. Other common choices include a strict market model (without an industry index) and a Fama-French 3 factor model. 
            Researchers may also include other factors considered relevant to the specific company at issue, such as a foreign market index, an exchange rate factor, or a factor for the spot price of a commodity (e.g., gold or silver).
            </p>
            <p>
            The period of time over which we train our chosen model is called the training window. Typically (though not always), we pick a training window before the news event so  
            the model is not affected by what we might consider "out-of-the-ordinary" price movements during the news announcement. 
            </p>
            <p>
            After we run our model on data over the training window, we get estimates for our model parameters: <MathJax inline>{"\\( \\hat \\alpha \\)"}</MathJax>, <MathJax inline>{"\\( \\hat \\beta_{\\text{Market}} \\)"}</MathJax>, and <MathJax inline>{"\\( \\hat \\beta_{\\text{Industry}} \\)"}</MathJax>.
            </p>
            <p className='heading2'>Predicted Return</p>
            <p>
            We use our parameter estimates to predict a baseline for what we think Big Pharma Co.'s stock return would have been solely based on market and industry movements. 
            If our return model captures the normal determinants of Big Pharma Co.'s stock return, then the predicted return serves as an estimate of the return that would have occurred absent the failed drug announcement. 
            We create this counterfactual return for each day during the event window, or the period of time during which we think the news was being released to the market. 
            </p>
            <p>
            Suppose the announcement of the failed drug occurred on day <MathJax inline>{"\\( s \\)"}</MathJax>. The predicted return on Big Pharma Co.'s stock on day <MathJax inline>{"\\( s \\)"}</MathJax> is given by 
              <MathJax className='math-container'>
                {`\\[
                  \\begin{aligned}
                      \\hat R_s = \\hat \\alpha + \\hat \\beta_{\\text{Market}}R_{ms} + \\hat \\beta_{\\text{Industry}}R_{is} 
                  \\end{aligned}
                \\]`}   
              </MathJax> 
            In words, we take the parameter estimates from our training model (<MathJax inline>{"\\( \\hat \\alpha \\)"}</MathJax>, <MathJax inline>{"\\( \\hat \\beta_{\\text{Market}} \\)"}</MathJax>, and <MathJax inline>{"\\( \\hat \\beta_{\\text{Industry}} \\)"}</MathJax>) and plug in the actual market (<MathJax inline>{"\\( R_{ms} \\)"}</MathJax>) and industry (<MathJax inline>{"\\( R_{is} \\)"}</MathJax>) returns on day <MathJax inline>{"\\( s \\)"}</MathJax> to predict what we think Big Pharma Co.'s stock return would have been 
            on day <MathJax inline>{"\\( s \\)"}</MathJax> had there been no failed drug announcement (<MathJax inline>{"\\( \\hat R_s \\)"}</MathJax>).
            </p>
            <p className='heading2'>Abnormal Return</p>
            <p>
            After we calculate the predicted return on day <MathJax inline>{"\\( s \\)"}</MathJax>, we can measure how much the actual return on day <MathJax inline>{"\\( s \\)"}</MathJax> differs from what we would have expected it to be in the absence of a failed drug announcement. To do this, 
            we simply subtract the predicted return (<MathJax inline>{"\\( \\hat R_{s} \\)"}</MathJax>) from the actual return (<MathJax inline>{"\\( R_s \\)"}</MathJax>) to get the abnormal return (AR).
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                  AR &= R_s - \\hat R_s \\\\
                  &= R_s - (\\hat \\alpha + \\hat \\beta_{\\text{Market}}R_{ms} + \\hat \\beta_{\\text{Industry}}R_{is} )
              \\end{aligned}
            \\]`}   
          </MathJax> 
            </p>
            <p className='heading2'>Event Window</p>
            <p>
            Our event window need not cover a single day only. We might want to estimate the abnormal return over several days if we believe that the news slowly disseminated through the market. 
            </p>
            <p>
            Typically, we choose a main event date and create a window around that date. We may add days before the event date to our window if we believe that there was information leakage, or that certain market participants found out about the news before the market as a whole and began trading on the information before the main event date. 
            We may add days after the main event date if we believe the information was incorporated into prices gradually rather than immediately. 
            </p>
            <p className='heading2'>Cumulative Abnormal Return</p>
            <p>
            If we have multiple days in our event window, we can find the full effect of the news by summing together the abnormal returns during the event window. This sum is called the cumulative abnormal return (CAR). 
            </p>
            <p>
            For example, suppose we believe that a few hedge funds found out a day early about Big Pharma Co.'s failed drug. Suppose as well that the market found out additional specifics about the future prospects of the drug the day after the initial announcement. 
            In this case, we might create an event window that includes days <MathJax inline>{"\\( s - 1\\)"}</MathJax>, <MathJax inline>{"\\( s \\)"}</MathJax>, and <MathJax inline>{"\\( s + 1 \\)"}</MathJax>. For each of these days, we compute 
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                AR_{s-1} &= R_{s-1} - \\hat R_{s-1} \\\\
                AR_s &= R_s - \\hat R_s \\\\
                AR_{s+1} &= R_{s+1} - \\hat R_{s+1}
              \\end{aligned}
            \\]`}   
          </MathJax> 
            Finally, we sum up the abnormal returns to get the cumulative abnormal return over the event window.
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                CAR = AR_{s-1} + AR_{s} + AR_{s+1}
              \\end{aligned}
            \\]`}   
          </MathJax> 
            </p>
            <p className='heading2'>Value of the Information</p>
            <p>
            The abnormal return measures how much the news changed the market's valuation of the company. For example, suppose we have a single day event window. The day 
            before the announcement of the failed drug, Big Pharma Co.'s stock was trading at $10 per share. The close of day trading price on the announcement date was $8.75 per share. If the predicted return on the event date is 
            <MathJax inline>{"\\( - 0.05 \\)"}</MathJax>, then the abnormal return is 
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                  AR &= R_s - \\hat R_s \\\\
                  &= (8.75/10 - 1) - (-0.05) \\\\
                  &= -0.075
              \\end{aligned}
            \\]`}   
          </MathJax> 
          If the company has 100 million shares outstanding, then we can estimate the total loss in market valuation attributable to the failed drug announcement as 
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                  \\overbrace{(P_{s-1}\\times N)}^{\\text{Mkt Cap}_{s-1}} |AR|  = 10(100)(0.075) = \\$75 \\text{ million}
              \\end{aligned}
            \\]`}   
          </MathJax> 
            </p>
            <p className='heading2'>Statistical Inference</p>
            <p>
            Even if there had been no failed drug announcement, we would not expect our prediction model to perfectly predict Big Pharma Co.'s stock return. Stock returns are inherently noisy, so abnormal returns will rarely equal exactly zero.
            </p>
            <p>
            One way to measure this background level of noise is the standard error of the regression from the training model. Roughly speaking, the standard error tells us how far actual returns typically deviate from the model's predictions on ordinary trading days.
            </p>
            <p>
            For a one-day event window, practitioners often use the regression standard error as an estimate of the standard error of the abnormal return. For an event window containing L trading days, a common approximation is
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                  SE(CAR) \\approx \\hat \\sigma_{\\varepsilon} \\sqrt{L}
              \\end{aligned}
            \\]`}   
          </MathJax> 
          where <MathJax inline>{"\\(  \\hat \\sigma_{\\varepsilon}  \\)"}</MathJax> is the standard error of the training regression.
            </p>
            <p>
            We can then compare the size of the abnormal return with its standard error by computing the t-statistic
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                  t = \\frac{AR}{SE(AR)}
              \\end{aligned}
            \\]`}   
          </MathJax> 
          or, for a multi-day event window,
          <MathJax className='math-container'>
            {`\\[
              \\begin{aligned}
                  t = \\frac{CAR}{SE(CAR)}
              \\end{aligned}
            \\]`}   
          </MathJax>  
            The larger the t-statistic is in absolute value, the stronger the evidence that the observed price movement reflects genuine news rather than the normal day-to-day fluctuations in stock prices.
            </p>
            <p className='heading2'>Significance Thresholds</p>
            <p>
            Although the exact cutoff depends on the application, practitioners often use the following guidelines:
            </p>
            <table class="minimal-table" style={{ maxWidth: "300px"}}>
            <thead>
              <tr>
                <th><MathJax inline>{"\\(  |t| \\)"}</MathJax></th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><MathJax inline>{"\\( < 1.645 \\)"}</MathJax></td>
                <td>Little evidence of an abnormal return</td>
              </tr>
              <tr>
                <td><MathJax inline>{"\\( \\approx 1.645 \\)"}</MathJax></td>
                <td>Significant at the 10% level</td>
              </tr>
              <tr>
                <td><MathJax inline>{"\\( \\approx 1.96 \\)"}</MathJax></td>
                <td>Significant at the 5% level</td>
              </tr>
              <tr>
                <td><MathJax inline>{"\\( \\approx 2.576 \\)"}</MathJax></td>
                <td>Significant at the 1% level</td>
              </tr>
            </tbody>
          </table>
          <p>
          The above thresholds assume a two-tailed test, which asks whether the news had either a positive or negative effect on the stock price. 
          Because either direction is possible, the evidence must be strong enough to rule out both possibilities. The familiar 5% significance threshold is approximately <MathJax inline>{"\\( |t| > 1.96 \\)"}</MathJax>.
          </p>
          <p>
          A one-tailed test asks whether the effect went in only one specified direction. For example, if we know in advance that a failed drug approval can only decrease firm value, we might test only for negative abnormal returns. 
          Because all of the statistical significance is concentrated in one tail of the distribution, the 5% threshold falls to approximately <MathJax inline>{"\\( |t| > 1.645 \\)"}</MathJax>.
          </p>
          <p>
          These inference formulas are approximations. More advanced event studies make additional adjustments for parameter estimation error, heteroskedasticity, overlapping event windows, and other statistical issues. 
          </p>
        </MathJaxContext>
        <PageNavigator group="ES"/>
        </div>
  );
}

export default ES1;